import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CampaignDetailsForm from 'components/forms/CampaignDetails/CampaignDetailsForm.tsx';
import Back from 'components/Back';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useCampaign from 'hooks/useCampaign';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import { updateCampaignDetails } from 'reducers/campaign/actions.ts';
import { CampaignDetails } from 'schemas/CampaignConfig.schema.ts';
import { ApiError } from 'schemas/ApiError.schema.ts';
import useUpdateCampaign from 'queries/mutate/updateCampaign.ts';
import { API_UPDATE_CAMPAIGN_ERROR } from 'constants/errorMessages';
import { ICampaignLevelParams } from 'utils/parameterUtils';
import useCampaignLock from 'hooks/useCampaignLock';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';

export function CampaignKeyDetailsPage() {
  const [error, setError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const { teamId, campaignId } = useParams() as ICampaignLevelParams;
  const { campaign } = useCampaign();
  const updateCampaign = useUpdateCampaign();
  const { lockCampaignIfRequired } = useCampaignLock({ teamId, campaignId });
  const initialRenderRef = useRef(true);

  const campaignTemplate = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(campaign.CampaignConfig.Template),
  });

  useProtectRoute();

  const handleSuccessfulSubmission = async (campaignDetails: CampaignDetails) => {
    if (!teamId) {
      setError({
        status: 500,
        message: 'Failed to create campaign. Your user is not associated with any teams, please contact a member of the SPI team.',
      });
      return;
    }

    const updatedCampaign = updateCampaignDetails(campaign, campaignDetails);

    if (!campaignId) {
      setError({
        status: 500,
        message: API_UPDATE_CAMPAIGN_ERROR,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await updateCampaign.mutateAsync({
        campaignId,
        teamId,
        campaign: updatedCampaign,
      });

      navigate(`/${teamId}/campaign/${campaignId}/key-details/confirmation`);
    } catch {
      setError({ message: API_UPDATE_CAMPAIGN_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      lockCampaignIfRequired();
    }
  }, [lockCampaignIfRequired]);

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Campaign summary" backTo={`/${teamId}/campaign/${campaignId}/summary`} />
      </div>
      <CampaignDetailsForm
        onSuccessfulSubmission={handleSuccessfulSubmission}
        isSubmitting={isSubmitting}
        campaign={campaign}
        template={campaignTemplate.data}
      />
      <ApiErrorDetails apiError={error} />
    </div>
  );
}

export default CampaignKeyDetailsPage;
